import Add16 from '@carbon/icons-react/es/add/16';
import Save16 from '@carbon/icons-react/es/save/16';
import {
  Button as RocketlaneButton,
  ButtonProps as RocketlaneButtonProps,
} from '@rocketlane/ui';
import React, { FC, forwardRef } from 'react';
import styled from 'styled-components';
import {
  ButtonProps,
  ButtonProps as LegacyButtonProps,
  Button as AntdButton,
} from 'components/atoms/button/button';
import { withHideIfNotAllowed } from 'components/organisms/permission-controller';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';

function mapAntdPropsToRadixProps(
  props: LegacyButtonProps,
): Omit<RocketlaneButtonProps, 'ref'> {
  const {
    type,
    size,
    icon,
    iconPlacement = 'left',
    loading,
    style,
    href,
    ...restProps
  } = props;
  const typeToVariantMap: Record<
    NonNullable<LegacyButtonProps['type']>,
    NonNullable<RocketlaneButtonProps['variant']>
  > = {
    primary: 'primary',
    default: 'secondary',
    dashed: 'secondary',
    link: 'link',
    ghost: 'ghost',
    tertiary: 'secondary',
    success: 'success',
    text: 'ghost',
  };
  const sizeMap: Record<
    NonNullable<LegacyButtonProps['size']>,
    NonNullable<RocketlaneButtonProps['size']>
  > = {
    small: 'medium', // Rocketlane UI medium is equivalent to AntD small
    middle: 'medium',
    large: 'large',
  };
  let rightIcon: React.ReactNode;
  let leftIcon: React.ReactNode;

  if (icon) {
    if (iconPlacement === 'right') {
      rightIcon = icon;
    } else {
      leftIcon = icon;
    }
  }
  const isLoading = loading ? Boolean(loading) : undefined;

  return {
    ...restProps,
    leftIcon,
    rightIcon,
    href,
    variant:
      'danger' in props && props.danger
        ? 'dangerSecondary'
        : typeToVariantMap[type || 'default'],
    size: size ? sizeMap[size] : 'medium',
    type: restProps.htmlType || 'button',
    loading: isLoading,
    ...(style && { style: style as React.CSSProperties }),
  } as Omit<RocketlaneButtonProps, 'ref'>;
}
const RlButton = React.forwardRef<HTMLButtonElement, LegacyButtonProps>(
  (props, ref) => {
    const radixProps = mapAntdPropsToRadixProps(props);
    const { href, ...buttonProps } = radixProps as Omit<
      RocketlaneButtonProps,
      'ref'
    > & { href?: string; target?: string; rel?: string };

    if (href) {
      return (
        <a
          href={href}
          target={buttonProps.target}
          rel={
            buttonProps.target === '_blank'
              ? buttonProps.rel ?? 'noopener noreferrer'
              : buttonProps.rel
          }
        >
          <RocketlaneButton ref={ref} {...buttonProps} />
        </a>
      );
    }

    return <RocketlaneButton ref={ref} {...buttonProps} />;
  },
);

RlButton.displayName = 'RlButton';

const InternalButton = forwardRef<HTMLButtonElement, LegacyButtonProps>(
  (props, ref) => {
    const isNewDesignEnabled = useAntdRlUISwitcher();

    if (isNewDesignEnabled) {
      return <RlButton ref={ref} {...props} />;
    }

    return <AntdButton ref={ref} {...props} />;
  },
);

InternalButton.displayName = 'InternalButton';

export const Button = styled(InternalButton)``;
export const SaveButton: FC<React.PropsWithChildren<ButtonProps>> = (props) => (
  <Button icon={<Save16 />} type="primary" iconPlacement="right" {...props} />
);

const BaseAddButton: FC<React.PropsWithChildren<ButtonProps>> = (props) => (
  <Button icon={<Add16 />} type="primary" iconPlacement="right" {...props} />
);

export const AddButton = withHideIfNotAllowed(BaseAddButton);

export const LinkButton = styled(Button).attrs({
  size: 'small',
  type: 'link',
})`
  font-size: inherit;
  height: auto;
  padding: 0;
`;
export const TertiaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => <Button ref={ref} type="tertiary" {...props} />,
);
TertiaryButton.displayName = 'TertiaryButton';
Button.displayName = 'Button';
